import { ApexOptions } from 'apexcharts';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { ComponentType, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Props as ReactApexChartProps } from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import { getBlueChartColorsSet } from '@/modules/common/utils/getBlueChartColorsSet.ts';
import { Spinner } from 'ui';
import { useWindowWidth } from 'ui/hooks';
import { goodFormatUSDCurrencyLowPrecision } from 'ui/utils';

const Chart = lazy(() =>
  import('react-apexcharts').then(module => ({
    default: ((module as any).default?.default ?? (module as any).default ?? module) as any,
  })),
) as unknown as ComponentType<ReactApexChartProps>;

interface BalancePieChartProps {
  options?: ApexOptions;
  series: ApexNonAxisChartSeries;
  labels: string[];
}

const BalancePieChart = ({ series, options = {}, labels }: BalancePieChartProps) => {
  const windowWidth = useWindowWidth() || 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperRefWidth, setWrapperWidth] = useState(0);

  const filteredSeries = useMemo<number[]>(() => {
    return (series as unknown[]).filter((item): item is number => typeof item === 'number' && !!item);
  }, [series]);

  const chartDonutWidth = useMemo(
    () => (windowWidth <= 768 ? wrapperRefWidth : wrapperRefWidth / 2),
    [windowWidth, wrapperRefWidth],
  );
  const chartHeight = useMemo(() => {
    return windowWidth <= 768 ? chartDonutWidth + (series?.length || 0) * 26 : chartDonutWidth; // todo: 26 need to be calculated [
  }, [chartDonutWidth, series?.length, windowWidth]);

  const handleSetWrapperWidth = useCallback(() => {
    if (!wrapperRef.current) return;

    setWrapperWidth(wrapperRef.current.getBoundingClientRect().width);
  }, []);

  useEffect(() => {
    handleSetWrapperWidth();
    window.addEventListener('resize', handleSetWrapperWidth);

    return () => {
      window.removeEventListener('resize', handleSetWrapperWidth);
    };
  }, [handleSetWrapperWidth, series]);

  const baseOptions = useMemo<ApexOptions>(() => {
    return {
      labels,
      responsive: [
        {
          breakpoint: 768,
          options: {
            width: chartDonutWidth,
            legend: {
              width: chartDonutWidth,
              floating: false,
              position: 'bottom',
              offsetX: 0,
              offsetY: 0,
              markers: { offsetY: 0 },
            },
          },
        },
      ],
      legend: {
        position: 'right',
        offsetX: -45,
        width: wrapperRefWidth / 2,
        itemMargin: { vertical: 6, horizontal: 0 },
        onItemClick: {
          toggleDataSeries: false,
        },
        markers: {
          width: 8,
          height: 8,
          offsetX: -5,
          offsetY: 2,
        },
      },
      colors: getBlueChartColorsSet(series?.length || 0),
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          donut: {
            size: '75%',
            labels: {
              show: true,
              name: { formatter: () => 'Current Asset Value' },
              value: { offsetY: -5 },
              total: {
                showAlways: true,
                show: true,
                color: '#737373',
                offsetY: -50,
                fontSize: '12px',
                formatter: ({ config }) => {
                  return goodFormatUSDCurrencyLowPrecision(
                    config.series.reduce((acc: number, curr: number) => acc + curr, 0),
                  );
                },
              },
            },
          },
        },
      },
      tooltip: {
        shared: false,
        cssClass: '!left-1/4',
        custom: ({ seriesIndex, series, w }) => {
          const label = w.config.labels[seriesIndex];
          return renderToString(
            <div className="flex flex-wrap gap-1 p-2 max-w-60">
              <div className="max-w-60 text-ellipsis overflow-hidden">{label}:</div>
              <div>{goodFormatUSDCurrencyLowPrecision(series[seriesIndex])}</div>
            </div>,
          );
        },
      },
      noData: {
        text: 'No data',
        align: 'center',
        offsetX: -(wrapperRefWidth / 4),
      },
      stroke: {
        show: filteredSeries.length > 1,
      },
    };
  }, [chartDonutWidth, filteredSeries.length, labels, series?.length, wrapperRefWidth]);

  const resolveOptions = useMemo(
    () => merge(cloneDeep(baseOptions), options),
    [baseOptions, options],
  );
  return (
    <div
      key={`chart_${wrapperRef.current?.getBoundingClientRect()?.width}_${series?.toString()}`}
      ref={wrapperRef}
      className={
        '[&>div]:ml-[-8px] [&>div>div>svg]:!overflow-auto [&>div>div>svg>foreignObject]:!overflow-auto [&_.apexcharts-legend>.apexcharts-legend-series]:flex ' +
        ' [&_.apexcharts-legend>.apexcharts-legend-series]:w-full [&_.apexcharts-legend-text]:w-full [&_.apexcharts-legend-text]:cursor-pointer'
      }
    >
      <Suspense
        fallback={
          <div className="min-h-[250px] h-full w-full flex justify-center items-center">
            <Spinner size="lg" />
          </div>
        }
      >
        <Chart
          type="donut"
          series={filteredSeries}
          options={resolveOptions}
          width={wrapperRefWidth}
          height={chartHeight}
        />
      </Suspense>
    </div>
  );
};

export default BalancePieChart;
